/**
 * Gestaltungsvorgaben als Werte.
 *
 * Bewusst als TypeScript und nicht als CSS: React Native kennt kein Stylesheet
 * aus einer `.css`-Datei. So benutzen Web und Mobile dieselben Zahlen, und eine
 * Änderung an der Farbe für „Einspeisung" wirkt an beiden Stellen.
 *
 * **Diese Datei ist die Quelle.** Das Design-System-Projekt auf claude.ai/design
 * ist ihr Spiegel: `pnpm --filter @hausmeter/ui design:emit` erzeugt daraus die
 * CSS-Dateien, die dort liegen. Nie umgekehrt — siehe ADR 0013.
 *
 * ## Die drei Farbachsen
 *
 * Sie werden ständig verwechselt, deshalb hier zuerst:
 *
 * 1. **Marke** (`brand`) — Logo, Navigation, Primäraktion, Fokusring.
 *    Erscheint **nie** in einer Datendarstellung.
 * 2. **Größe** (`QUANTITY_COLORS`) — Netzbezug, Einspeisung, Erzeugung …
 *    Die **einzigen** Serienfarben in Diagrammen, Kacheln und Legenden.
 * 3. **Sparte** (`COMMODITY_COLORS`) — Strom, Wasser, Wärme, Gas.
 *    Nur zur Wegweisung: Symbol, Reiter, Karten-Akzentstreifen. Nie eine Serie.
 *
 * Achse 2 und 3 sind farblich disjunkt, damit eine Verwechslung auffällt statt
 * sich zu tarnen. Ein Test sichert das ab.
 */

/**
 * Neutrale Rampe. Alle Flächen-, Rand- und Textfarben stammen hieraus — eine
 * einzige Grauleiter statt zweier, die sich um wenige Prozent unterscheiden.
 */
export const neutral = {
  0: "#ffffff",
  50: "#f8fafc",
  100: "#f1f5f9",
  200: "#e2e8f0",
  300: "#cbd5e1",
  400: "#94a3b8",
  500: "#64748b",
  600: "#475569",
  700: "#334155",
  800: "#1e293b",
  900: "#0f172a",
} as const;

/**
 * Die Marke. Abgeleitet aus dem Logo: grünes Quadrat mit Haus, gelber Blitz.
 *
 * `primary` und `colors.selfConsumption` sind beide grün und liegen dicht
 * beieinander. Das ist Absicht — sie treffen nie aufeinander, weil die Marke in
 * keiner Datendarstellung vorkommt.
 */
export const brand = {
  /** Logo-Grün. Primäraktion, aktive Navigation, Fokusring. */
  primary: "#1aa84f",
  primaryHover: "#128a41",
  primaryInk: "#0f572d",
  /** Der Blitz aus dem Logo. Sparsamer Akzent, nie eine große Fläche. */
  accent: "#fbcb1e",
  /** Ruhige Hinterlegung für aktive Navigationseinträge. */
  wash: "#ecfaf0",
} as const;

/**
 * Die Rampe der Marke — Grün und Gelb in Stufen. Aus dem Logo abgeleitet, nicht
 * berechnet. Die Vollstufen sind `brand`; die Tints (50–300) tragen Hinterlegungen
 * („aktiv" in der Navigation), die Tiefen (700–900) die Schrift darauf.
 *
 * **Sie steht hier und nicht mehr nur im Erzeuger**, weil das dunkle Thema sie
 * spiegelt: Auf dunklem Grund tauschen Tint und Tiefe die Rollen (`dark.brandRamp`).
 */
export const brandRamp = {
  green: {
    50: brand.wash,
    100: "#d1f3dc",
    200: "#a6e6bd",
    300: "#6fd394",
    400: "#38bd6c",
    500: brand.primary,
    600: brand.primaryHover,
    700: "#0f6f36",
    800: brand.primaryInk,
    900: "#0c3f22",
  },
  yellow: {
    50: "#fef9e6",
    100: "#fdf0bf",
    200: "#fbe285",
    300: "#fad251",
    400: brand.accent,
    500: "#e6b40a",
    600: "#b88a06",
  },
} as const;

export const colors = {
  /** Bezug aus dem Netz — das, was Geld kostet. */
  gridImport: "#c2410c",
  /** Einspeisung ins Netz. */
  gridExport: "#0369a1",
  /** Erzeugung der Anlage. */
  generation: "#ca8a04",
  /** Eigenverbrauch — die Schnittmenge aus Erzeugung und Verbrauch. */
  selfConsumption: "#15803d",
  /** Verbrauch gesamt. */
  consumption: neutral[700],
  /**
   * Der Speicher (HM-122): was in ihn geht und was aus ihm kommt — **eine**
   * Größe in zwei Stufen, keine zwei Größenfarben. Violett, weil es keine der
   * fünf gelernten Größen ist und neben Gelb (Erzeugung) und Grün
   * (Eigenverbrauch) unterscheidbar bleibt; die Ladung in der tieferen Stufe.
   */
  storage: "#5b21b6",
  storageDischarge: "#8b5cf6",
  /**
   * Eine Reihe **ohne gedeutete Energiegröße** (HM-054): der Rohverlauf eines
   * Datenpunkts — Spannung, Strom, Frequenz, ein Zählerstand ohne Rolle.
   *
   * Sie steht hier, weil die Alternative schlechter ist: Bis HM-054 zeichnete
   * das Verlaufsdiagramm eines Datenpunkts in **Markengrün** und brach damit
   * die erste der drei Farbachsen (ADR 0013). Eine der Größenfarben zu leihen
   * wäre der zweite falsche Weg — sie sind fachlich belegt, und eine Spannung
   * in Netzbezugs-Orange wäre eine Aussage, die niemand gemeint hat.
   *
   * Der Wert ist nicht geraten: Er ist die einzige Farbe, die neben der
   * vollständigen Größenpalette **alle** Prüfungen auf Farbfehlsichtigkeit und
   * Unterscheidbarkeit besteht (`ΔE`-Prüfung über alle Paare, siehe HM-054).
   */
  datapoint: "#7e22ce",

  surface: neutral[0],
  surfaceMuted: neutral[50],
  surfaceSunken: neutral[100],
  border: neutral[200],
  borderStrong: neutral[300],
  text: neutral[900],
  textMuted: neutral[500],
  textFaint: neutral[400],
  textInverted: neutral[0],
  /**
   * Kurzhinweis am Zeiger: eine dunkle Fläche im hellen Bild, eine helle im
   * dunklen — die Umkehrung des Themas, nicht seine Fortsetzung.
   */
  surfaceTooltip: neutral[900],
  textOnTooltip: neutral[0],
  /**
   * Der Knopf eines Schalters. Nicht „weiß", sondern „das Helle auf dem Zug":
   * Im dunklen Thema bleibt er hell, denn der Zug darunter ist Markengrün oder
   * ein Rand — beides in beiden Themen dunkler als er.
   */
  surfaceKnob: neutral[0],

  warning: "#b45309",
  danger: "#b91c1c",
  success: "#15803d",
  info: "#0369a1",
  /** Lücken in einer Reihe — sichtbar, aber nicht alarmierend. */
  gap: neutral[300],
} as const;

export type ColorToken = keyof typeof colors;

/** Fläche, Rand und Schrift eines Hinweistons — als Paar, nie einzeln gemischt. */
export interface Tone {
  readonly bg: string;
  readonly border: string;
  readonly fg: string;
}

export type ToneName = "success" | "warning" | "danger" | "info";

/**
 * Tonpaare für Hinweisflächen — `Alert`, `Badge`, Warnkästen.
 *
 * Fläche, Rand und Schrift eines Tons gehören zusammen; einzeln gemischt
 * entstehen sonst in jedem Bild andere Kombinationen — und bis HM-107 standen
 * die Flächen als Hexwerte in vier Dateien, und keine davon hätte ein dunkles
 * Thema erreicht.
 */
export const tones: Readonly<Record<ToneName, Tone>> = {
  success: { bg: brandRamp.green[50], border: brandRamp.green[200], fg: brandRamp.green[700] },
  warning: { bg: "#fdf3e4", border: "#f3ddb8", fg: colors.warning },
  danger: { bg: "#fdeceb", border: "#f4c8c5", fg: "#b12a25" },
  info: { bg: "#e8f2fa", border: "#bcd8ee", fg: "#0f4d7a" },
};

/**
 * Farbe je Sparte. **Nur zur Wegweisung** — Symbol, Reiter, Akzentstreifen einer
 * Karte. Niemals als Serienfarbe in einem Diagramm: dort gilt ausschließlich
 * `QUANTITY_COLORS`, sonst stünde in einem Strom-Diagramm gelb einmal für die
 * Sparte und einmal für die Erzeugung.
 */
export const COMMODITY_COLORS = {
  electricity: brand.accent,
  water: "#2e90d9",
  heat: "#e8683a",
  gas: "#7c3aed",
} as const;

export type CommodityColorKey = keyof typeof COMMODITY_COLORS;

/** Die sechs Größen des Hauses — der Kern der Größenpalette, je Thema einmal. */
export interface QuantityBase {
  readonly consumption: string;
  readonly gridImport: string;
  readonly gridExport: string;
  readonly generation: string;
  readonly selfConsumption: string;
  readonly commonArea: string;
  /** Der Speicher in zwei Stufen (HM-122): Ladung tiefer, Entladung heller. */
  readonly storage: string;
  readonly storageDischarge: string;
  /**
   * Die Abstufungen des Verbrauchs, wenn er aufgeteilt wird (HM-118): zweiter
   * und dritter Verbraucher, zuletzt der gerechnete Rest. **Eine
   * Helligkeitsreihe derselben Größe**, keine weiteren Größenfarben — ein
   * Wallbox-Streifen ist Verbrauch und kein Netzbezug. Der Rest steht in der
   * blassesten Stufe, weil er nicht gemessen, sondern gerechnet ist.
   */
  readonly consumptionShades: readonly [second: string, third: string, rest: string];
}

/**
 * Aus den sechs Größen des Hauses die vollständige Palette.
 *
 * Die Größen je Wohneinheit und Verbraucher (HM-080) tragen die Farbe der
 * Größe, die sie **sind**: Ein PV-Anteil ist Eigenverbrauch, ein Netzanteil
 * ist Netzbezug, ein Verbrauch ist Verbrauch. Eine eigene Farbe je Wohnung
 * wäre eine Aussage, die niemand gemeint hat (ADR 0013). Die Ableitung steht
 * einmal, damit das dunkle Thema sie nicht abschreiben muss.
 */
export function quantityPalette(base: QuantityBase): Readonly<Record<string, string>> {
  return {
    consumption: base.consumption,
    grid_import: base.gridImport,
    grid_export: base.gridExport,
    generation: base.generation,
    self_consumption: base.selfConsumption,
    common_area: base.commonArea,
    unit_consumption: base.consumption,
    unit_pv_share: base.selfConsumption,
    unit_grid_share: base.gridImport,
    load_consumption: base.consumption,
    load_pv_share: base.selfConsumption,
    /** Der Haushalt ohne die gemessenen Verbraucher (HM-110) ist Verbrauch — nur weniger davon. */
    residual_consumption: base.consumption,
    storage_charge: base.storage,
    storage_discharge: base.storageDischarge,
    /* Keine Größen, sondern Stufen der Größe Verbrauch (HM-118) — nur im Stapel
       seiner Aufteilung, nie neben einer anderen Größe. */
    consumption_2: base.consumptionShades[0],
    consumption_3: base.consumptionShades[1],
    consumption_rest: base.consumptionShades[2],
  };
}

/** Farbe je Messgröße — dieselbe Zuordnung in Diagrammen, Kacheln und Legenden. */
export const QUANTITY_COLORS: Readonly<Record<string, string>> = quantityPalette({
  ...colors,
  commonArea: colors.textMuted,
  consumptionShades: [neutral[500], neutral[400], neutral[300]],
});

export function colorForQuantity(quantityKey: string): string {
  return QUANTITY_COLORS[quantityKey] ?? colors.consumption;
}

/**
 * Dieselbe Farbe als CSS-Variable — `var(--hm-quantity-grid-import)`.
 *
 * **Im DOM ist das der richtige Weg, `colorForQuantity` der falsche** (HM-107):
 * Ein fester Hexwert im `style`-Objekt trägt das helle Thema in das dunkle;
 * die Variable wird vom Thema aufgelöst. `colorForQuantity` bleibt für alles,
 * was keine Variable auflösen kann — ein Canvas ohne Element, React Native.
 * Eine unbekannte Größe fällt auf den Verbrauch zurück, wie dort.
 */
export function quantityColorVar(quantityKey: string): string {
  const key = quantityKey in QUANTITY_COLORS ? quantityKey : "consumption";
  return `var(--hm-quantity-${key.replaceAll("_", "-")})`;
}

/**
 * Farbe einer Sparte. Fällt bewusst auf Grau zurück statt auf Strom: eine
 * unbekannte Sparte soll unauffällig aussehen, nicht wie eine bekannte.
 */
export function colorForCommodity(commodity: string): string {
  return COMMODITY_COLORS[commodity as CommodityColorKey] ?? colors.textMuted;
}

/** Dieselbe Farbe als CSS-Variable; eine unbekannte Sparte wird `var(--text-muted)`. */
export function commodityColorVar(commodity: string): string {
  return commodity in COMMODITY_COLORS ? `var(--hm-commodity-${commodity})` : "var(--text-muted)";
}

/**
 * Die Abstandsleiter. Vielfache von 4, aber keine lückenlose Multiplikation —
 * oberhalb von 24 werden die Sprünge größer, weil dort niemand mehr 28 von 32
 * unterscheidet.
 */
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 32,
  8: 40,
  9: 48,
  10: 64,
  12: 80,
} as const;

/** Benannte Auswahl aus `space` für den Alltag. */
export const spacing = {
  xs: space[1],
  sm: space[2],
  md: space[4],
  lg: space[6],
  xl: space[7],
  xxl: space[9],
} as const;

export const radius = {
  sm: 4,
  md: 8,
  lg: 12,
  /** Karten. */
  xl: 16,
  /** Dialoge. */
  xxl: 24,
  pill: 999,
} as const;

export const fontSize = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  /** Für den einen großen Wert auf dem Übersichtsbildschirm. */
  display: 40,
} as const;

/**
 * Schriftfamilien. Self-hosted auszuliefern, nicht über Google Fonts: ein
 * Abruf von fremden Servern beim Seitenaufruf ist bei einem Produkt, das
 * Local-First verspricht, das falsche Signal — und datenschutzrechtlich heikel.
 * Siehe `docs/licenses/THIRD_PARTY.md`.
 *
 * **Der erste Name ist der, unter dem `@fontsource-variable` die Schrift
 * registriert — mit „Variable" dahinter.** Bis HM-024 stand hier nur `Sora`, und
 * die Schrift kam nirgends an: nicht in der Web-App, nicht auf der Webseite.
 * Die Dateien wurden ausgeliefert, der Browser suchte aber eine Familie, die
 * keine `@font-face`-Regel nennt, und nahm still den Rückfall. Aufgefallen ist es
 * erst auf einem Bildschirmfoto. `theme.test.ts` hält die Namen seither gegen die
 * Pakete. Der schlichte Name bleibt dahinter — für eine lokal installierte Schrift
 * und für das Design-System-Projekt, das keine Pakete lädt.
 */
export const fontFamily = {
  display: "'Sora Variable', Sora, 'Segoe UI', system-ui, sans-serif",
  body: "'Manrope Variable', Manrope, 'Segoe UI', system-ui, sans-serif",
  /** **Jede Zahl** steht in dieser Schrift: gleiche Ziffernbreite, damit
   *  eine Zahlenspalte nicht springt, während sie sich aktualisiert. */
  mono: "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, 'SF Mono', monospace",
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

export const lineHeight = {
  tight: 1.12,
  snug: 1.3,
  normal: 1.5,
  relaxed: 1.65,
} as const;

/**
 * Laufweite in `em`. Als Zahl und nicht als `"-0.02em"`, weil React Native
 * `letterSpacing` in Punkten erwartet: dort `fontSize * letterSpacing`.
 */
export const letterSpacing = {
  tight: -0.02,
  snug: -0.01,
  normal: 0,
  wide: 0.04,
  /** Für die kleinen Großbuchstaben-Beschriftungen über einem Wert. */
  caps: 0.08,
} as const;

/**
 * Schatten. **Nur Web** — React Native braucht `elevation` bzw. eigene
 * Schatten-Eigenschaften und kann diese Zeichenketten nicht lesen.
 */
export const shadow = {
  xs: "0 1px 2px rgba(15, 23, 42, 0.06)",
  sm: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
  md: "0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)",
  lg: "0 12px 28px rgba(15, 23, 42, 0.12), 0 4px 8px rgba(15, 23, 42, 0.05)",
  /** Liegt ausschließlich unter der Primäraktion. */
  brand: "0 6px 18px rgba(26, 168, 79, 0.28)",
} as const;

/** Maße des Web-Gerüsts. Auf Mobile ohne Bedeutung — dort gilt `appLayout`. */
export const layout = {
  containerMax: 1200,
  sidebarWidth: 248,
  /**
   * Die eingeklappte Seitenleiste — die **Schiene**. Kit: `width: collapsed ? 64
   * : 248` in `Sidebar.jsx`.
   *
   * Die Zahl ist kein gerundeter Rest: Ein Navigationseintrag ist eingeklappt
   * 40 Pixel breit, und 64 lässt ihm links und rechts denselben Rand wie die
   * ausgeklappte Leiste ihrem Text. Wer sie auf 56 zieht, drückt die Symbole an
   * die Kante; wer sie auf 72 zieht, gewinnt eine Spalte, die nichts trägt.
   */
  sidebarRailWidth: 64,
  headerHeight: 64,
  focusRingWidth: 3,
} as const;

/**
 * Die Punkte, an denen das Gerüst seine Form ändert. **Nur Web.**
 *
 * Die Zahlen stammen aus `ui_kits/dashboard/` — dort steht an jeder Stelle ein
 * `useNarrow(…)` mit genau diesen Werten. Sie sehen willkürlich aus und sind es
 * nicht: Sie liegen dort, wo die jeweilige Anordnung tatsächlich bricht, und
 * nicht auf den Kantenlängen bekannter Geräte. Ein Bruchpunkt bei „iPhone" wäre
 * beim nächsten Gerät falsch und dazwischen ohnehin.
 *
 * **Gemessen wird das Fenster, nicht der Bereich daneben.** Das ist eine
 * Vereinfachung, die nur trägt, weil das Gerüst dieselbe Seitenleiste hat wie
 * das Kit: Unterhalb von `shell` gibt es sie gar nicht mehr, darüber ist sie
 * immer 248 Pixel breit. Wer eine Ansicht baut, deren Breite davon abweicht,
 * misst besser den Bereich selbst — mit `minmax(min(100%, …), 1fr)`, das ohne
 * jede Zahl auskommt und deshalb überall dort vorzuziehen ist, wo sich die
 * Anordnung nicht in der Zahl der Elemente unterscheidet.
 */
export const breakpoints = {
  /**
   * Darunter wird die Seitenleiste zur Schublade, und über dem Inhalt steht
   * eine Zeile mit Marke und Menüknopf. Kit: `useNarrow(860)` in `index.html`
   * und `TopBar.jsx`.
   */
  shell: 860,
  /**
   * Darunter stapeln zweispaltige Kartenpaare. Sie sind ungleich breit
   * (`2fr 1fr`) und lassen sich deshalb **nicht** über `auto-fit` ausdrücken.
   * Kit: `useNarrow(1040)` in `OverviewScreen.jsx`.
   */
  split: 1040,
  /**
   * Darunter legt eine Liste ihre Nebenspalten in die erste Zelle. Kit:
   * `useNarrow(760)` in `DevicesScreen.jsx`.
   */
  table: 760,
  /**
   * Darunter lösen Kachelreihen ihr festes Raster auf. Kit: `useNarrow(620)`
   * in `OverviewScreen.jsx`.
   */
  tiles: 620,
  /**
   * Darunter stellt sich der Zeitraum-Wähler von nebeneinander auf
   * untereinander. Kit: `useNarrow(560)` in `TopBar.jsx`.
   */
  picker: 560,
} as const;

/**
 * Das Gerüst der App (iOS, Android) — Telefon **und** Tablet (HM-175, ADR 0052).
 *
 * **Zwei Fensterklassen, nicht ein Gerätekatalog.** Gemessen wird die Breite des
 * **Fensters** und nicht das Gerät: Ein iPad in schmaler Split View ist ein
 * Telefon, ein Foldable aufgeklappt ein Tablet. Unter `regular` ist die App
 * *kompakt* — Reiterleiste unten, Inhalt über die volle Breite, wie bisher. Ab
 * `regular` steht die Navigation als **Schiene** links, der Inhalt in einer
 * Spalte, die nicht breiter wird als ihre Aufgabe. Die Schwelle ist die von
 * Material 3 („medium", 600 dp); ein iPad mini hochkant liegt mit 744 pt
 * darüber, das breiteste Telefon quer mit rund 440 pt Inhaltsbreite darunter.
 *
 * **Die Spaltenbreiten sind Lesebreiten, keine Gerätemaße.** `form` für die
 * Anmeldung und Formulare (ein Feld über 1000 pt liest niemand in einem Zug),
 * `reading` für Listen und Detailseiten, `wide` für Seiten mit Karten im
 * Raster. Wie viele Karten nebeneinanderstehen, folgt aus `columnMin` und der
 * tatsächlich verfügbaren Breite — nicht aus der Fensterklasse.
 */
export const appLayout = {
  /** Ab dieser Fensterbreite (pt) ist die App regulär: Schiene statt Reiterleiste. */
  regular: 600,
  /** Navigationsschiene links (Material 3 Navigation Rail). */
  railWidth: 80,
  /** Seitenrand regulär; kompakt bleibt er bei 16. */
  gutter: 24,
  /** Inhaltsspalte für Anmeldung und Formulare. */
  form: 480,
  /** Inhaltsspalte für Listen, Einstellungen und Detailseiten. */
  reading: 720,
  /** Inhaltsspalte für Seiten mit Karten im Raster — „Strom", „Mein Strom". */
  wide: 1040,
  /** Schmaler wird eine Karte im Raster nicht; sonst rückt sie in die nächste Zeile. */
  columnMin: 320,
  /** Ein Blatt von unten wird nicht breiter (Material 3 Bottom Sheet). */
  sheetMax: 640,
} as const;

/**
 * Der Grund hinter etwas, das den Rest stilllegt — Dialog und Schublade.
 *
 * Steht als Wert und nicht zweimal als Zeichenkette: Der Dialog bekommt ihn
 * über `dialog::backdrop` aus dem Stylesheet (ein Pseudoelement erreicht kein
 * `style`-Objekt), die Schublade über `var(--overlay-scrim)`. Zwei Fassungen
 * derselben Farbe laufen beim ersten Nachjustieren auseinander.
 */
export const overlay = {
  scrim: "rgb(15 23 42 / 45%)",
} as const;

/**
 * Das dunkle Thema (HM-107, ADR 0030).
 *
 * **Ein Umschalter, keine zweite Farbwelt.** Gesetzt wird `data-theme="dark"`
 * auf `<html>`; überschrieben werden die neutralen Stufen, die Tint- und
 * Tiefstufen der Marke, die semantischen Aliase — und die Serien- und
 * Spartenfarben in einer **helleren** Fassung. Ocker und Dunkelgrün trennen
 * auf dunklem Grund schlechter als auf hellem (OQ-014); die Farbtöne bleiben
 * dieselben, damit, wer Orange als Netzbezug gelernt hat, es wiedererkennt.
 *
 * **Die Vollstufe der Marke bleibt.** Ein Primärknopf ist in beiden Fassungen
 * dasselbe Grün; getauscht werden nur Tint und Tiefe darum herum — „aktiv"
 * liegt hell als `green-50` hinter dem Eintrag und als `green-700` darauf, und
 * auf dunklem Grund muss das Paar die Rollen wechseln, sonst leuchtet eine
 * weiße Fläche in der Navigation.
 *
 * Die Werte stehen hier als Zahlen und nicht nur im Stylesheet, aus demselben
 * Grund wie alles in dieser Datei: React Native liest kein CSS.
 */
export const dark = {
  neutral: {
    0: "#0b1220",
    50: "#0f1725",
    100: "#17202f",
    200: "#222c3e",
    300: "#313d53",
    400: "#6b7b93",
    500: "#8fa0b6",
    600: "#adbbcd",
    700: "#c9d4e2",
    800: "#e1e8f1",
    900: "#f2f6fb",
  },
  /** Tints werden Tiefen, Tiefen werden Tints. 500 fehlt: Sie bleibt. */
  brandRamp: {
    green: {
      50: "#10281c",
      100: "#16382a",
      200: "#1d5138",
      300: "#2a7a4f",
      400: "#38bd6c",
      600: "#5ecb88",
      700: "#8fdda9",
      800: "#b6e9c7",
      900: "#d9f5e2",
    },
    yellow: { 50: "#2a2208", 100: "#3b300b", 600: "#f6d264" },
  },
  text: {
    strong: "#f2f6fb",
    body: "#c9d4e2",
    muted: "#8fa0b6",
    faint: "#7c8ba3",
    /** Unverändert: Die Fläche darunter ist das Markengrün, nicht das Thema. */
    invert: "#ffffff",
    brand: "#5ecb88",
    onTooltip: "#0a101c",
  },
  /** Die Karte liegt über der Seite, also heller — die Umkehrung des hellen Themas. */
  surface: {
    page: "#0a101c",
    card: "#141d2c",
    sunken: "#1d2738",
    inset: "#111a28",
    brand: brand.primary,
    /**
     * Die tiefe Markenfläche — die Markenfläche der Anmeldeseite, der Chip der
     * Wortmarke. Hell ist das `green-600`; dunkel bleibt es **derselbe Wert**,
     * denn dort ist `green-600` ein Tint, und weiße Schrift darauf war nicht
     * mehr zu lesen. Eine Markenfläche gehört zur Marke, nicht zum Thema.
     */
    brandStrong: brand.primaryHover,
    dark: "#050a13",
    tooltip: "#f2f6fb",
    knob: "#ffffff",
  },
  border: { subtle: "#222c3e", default: "#2d3950", strong: "#44536d", brand: brand.primary },
  focusRing: "color-mix(in srgb, #38bd6c 55%, transparent)",
  /** Achse 2, heller gefasst — dieselben Töne wie im hellen Thema. */
  quantities: {
    consumption: "#c9d4e2",
    gridImport: "#fb9a4b",
    gridExport: "#4cb8ea",
    generation: "#f2c33c",
    selfConsumption: "#4ec97d",
    commonArea: "#94a3b8",
    /* Nicht das Violett der Sparte Gas (`commodities.gas`): Sparten- und
       Größenfarben bleiben auch dunkel auseinander. */
    storage: "#b08cf9",
    storageDischarge: "#d0bcff",
    /* Der Entwurf setzt den Rest dunkel auf #394a5f — 1,9:1 gegen die Karte und
       damit unter der Grenze für Grafik (WCAG 1.4.11), die jede dunkle Größe
       hält. Die Leiter ist deshalb nach oben gerückt: dieselbe Richtung, der
       Rest bleibt die blasseste Stufe, und keine fällt unter 3:1. */
    consumptionShades: ["#8ba0b8", "#73889f", "#5c7089"],
  } satisfies QuantityBase,
  datapoint: "#c084fc",
  /** Achse 3. Strom bleibt das Gelb des Logos; es ist auf beiden Gründen lesbar. */
  commodities: {
    electricity: brand.accent,
    water: "#4fb0ef",
    heat: "#f58a5e",
    gas: "#a78bfa",
  } satisfies Record<CommodityColorKey, string>,
  gap: "#3a4760",
  quality: { interpolated: "#7c8ba3", estimated: "#7c8ba3", suspect: "#e0a94a" },
  status: { success: "#4ec97d", warning: "#e9a83c", danger: "#f0706b", info: "#4cb8ea" },
  tones: {
    success: { bg: "#10281c", border: "#1d5138", fg: "#8fdda9" },
    warning: { bg: "#2d2209", border: "#4a3a13", fg: "#f6cf7a" },
    danger: { bg: "#2e1416", border: "#4d2024", fg: "#f7a8a4" },
    info: { bg: "#0e2433", border: "#1b3d54", fg: "#8ed0f2" },
  } satisfies Record<ToneName, Tone>,
  /** Auf dunklem Grund trägt Tiefe, nicht Streuung. */
  shadow: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.4)",
    sm: "0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.3)",
    md: "0 4px 12px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)",
    lg: "0 12px 28px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.35)",
    brand: "0 6px 18px rgba(26, 168, 79, 0.35)",
  } satisfies Record<keyof typeof shadow, string>,
  /** Die Übergangsaliase, solange es sie gibt. */
  legacyInk: { strom: "#f2c33c", wasser: "#8ed0f2", heizung: "#f4a184" },
} as const;

/** Die Größenpalette des dunklen Themas — vollständig, wie `QUANTITY_COLORS`. */
export const DARK_QUANTITY_COLORS: Readonly<Record<string, string>> = quantityPalette(
  dark.quantities,
);
